import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Toaster } from 'sonner';
import {
  SessionProvider,
  setGameIdInURL,
  useSession,
  onSessionExpired,
  resetSessionExpired,
  getDemoOfferIdFromURL,
  getViewFromURL,
} from './modules/session';
import { ThemeProvider, useTemplate } from './modules/theme';
import { WalletProvider, useWallet } from './modules/wallet';
import { PaymentsProvider } from './modules/payments/PaymentsProvider';
import type { FundingMode } from './modules/payments/types';
import { AuthProvider } from './modules/auth/AuthProvider';
import { useAuth } from './modules/auth/useAuth';
import {
  ErrorBoundary,
  LoadingScreen,
  ExpiredScreen,
  useView,
  decideInitialView,
  decideExpiryView,
} from './modules/shell';
import { emit } from './modules/postMessage';
import { useNavigateBackListener } from './modules/backStack';
import { useEnableHapticsListener } from './modules/haptics';
import { createApiClient, getDesignSystem } from './modules/api';
import type { DesignSystemResult, RegistrationInfo } from './modules/api/endpoints/designSystem';
import { ensureFooterLinks } from './modules/footerLinks';
import { saveShippingDisabled } from './modules/shipping';
import { useLaunchGameKind } from './modules/game/useGameKind';
import { readLabPreviewScene } from './modules/customPack/preview/labPreviewScene';
import { saveOperatorLinks } from './modules/operatorLinks';
import { isOpenerVariant } from '@game-platform/pack-animation';
import { saveOpeningAnimation } from './modules/openingAnimation';
import { saveDemoEmbedKey } from './modules/demo';
import { clearTickets, saveFairnessEnabled } from './modules/fairness';
import { initTracking } from './modules/tracking';
import { preloadOpener } from './modules/reveal/opener/loadOpener';
import { preloadForPackOverrides } from './modules/reveal/opener/preloadForPacks';
import { config } from './config';
import {
  CUSTOM_PACK_ROUTE,
  useChildListener,
  useClickToEdit,
  useReportPageRoute,
} from '@game-platform/theme-constructor';
import { Lobby as ClassicLobby } from './templates/classic/Lobby';
import { PackPage as ClassicPackPage } from './templates/classic/Pack';
import { PackPage as ModernPackPage } from './templates/modern/Pack';
import { PackPage as SleekPackPage } from './templates/sleek/Pack';
import { PackPage as GlassPackPage } from './templates/glass/Pack';
import { ShippingInner } from './pages/Shipping';

const CAROUSEL_PACK_PAGES = {
  modern: ModernPackPage,
  sleek: SleekPackPage,
  glass: GlassPackPage,
} as const;

const DevConsole = lazy(() =>
  import('./modules/dev').then((m) => ({ default: m.DevConsole })),
);

const DesignSystemPage = lazy(() =>
  import('./pages/DesignSystem').then((m) => ({ default: m.DesignSystemPage })),
);

const AnimationPreviewPage = lazy(() =>
  import('./modules/animation-preview').then((m) => ({ default: m.AnimationPreviewPage })),
);

// The Design Constructor's lab-state preview: loaded only by a preview that asks for a lab state.
const LabPreviewPage = lazy(() =>
  import('./modules/customPack/preview').then((m) => ({ default: m.LabPreviewPage })),
);

// The custom pack lab: loaded only by a launch that opens one, never by a loot pack's page.
const loadCustomPackLab = () => import('./modules/customPack');
const CustomPackLabPage = lazy(() => loadCustomPackLab().then((m) => ({ default: m.CustomPackLabPage })));

function Inner() {
  const session = useSession();
  // Stable identity from the provider — destructured so the effects below
  // depend on the callback itself, not on the whole session object.
  const { dropToken } = session;
  const wallet = useWallet();
  const { requestAuth } = useAuth();
  const { view, goLobby, goGame, goExpired, goAnonymous } = useView();
  const didInit = useRef(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);

  const template = useTemplate();

  const client = useMemo(
    () =>
      createApiClient({
        baseUrl: config.apiUrl,
        getToken: session.getToken,
        getEmbedKey: () => session.embedKey,
      }),
    [session.getToken, session.embedKey],
  );

  // A lab state the Design Constructor preview asks for (`?lab_scene=`, child mode only); null everywhere else.
  const [labScene] = useState(readLabPreviewScene);
  // Which page the launched game gets, from what the tab already knows: only a known custom pack (or a preview of a
  // lab state) is looked up first, every other launch renders its pack page at once. With neither a token nor an
  // embed key the boot below ends on the Expired screen, and a lookup would only be turned away with a 401 that
  // tells the host the session is gone a second time.
  const gameKind = useLaunchGameKind(
    client,
    session.token || session.embedKey ? session.gameId : null,
    labScene !== null,
  );
  // The lab's code downloads while its offer is looked up, not after.
  useEffect(() => {
    if (gameKind.kind === 'custom_pack') void loadCustomPackLab().catch(() => undefined);
  }, [gameKind.kind]);
  // The lab shares `/` with the pack page: the Theme Constructor preview learns it is on the lab from this.
  useReportPageRoute(gameKind.kind === 'custom_pack' || labScene !== null ? CUSTOM_PACK_ROUTE : null);

  useEffect(() => {
    if (didInit.current) return;

    if (!session.token) {
      // No token routes before the wallet's first fetch — embed mode browses
      // the public catalog anonymously (auth gates open the modal later);
      // operator/partner mode has nothing to show, so it goes Expired.
      didInit.current = true;
      const target = decideInitialView({
        hasToken: false,
        hasEmbedKey: session.embedKey != null,
        walletError: false,
        hasGameId: session.gameId != null,
      });
      if (target === 'anonymous') {
        goAnonymous();
      } else {
        emit('session_expired');
        goExpired();
      }
      return;
    }

    // Wait for WalletProvider's first balance fetch to settle before routing.
    if (wallet.loading) return;

    didInit.current = true;

    const target = decideInitialView({
      hasToken: true,
      hasEmbedKey: session.embedKey != null,
      // A wallet error here is most likely a 401 / invalid token (backend
      // converted auth failure to a NetworkError-free string).
      walletError: Boolean(wallet.error),
      hasGameId: session.gameId != null,
    });

    if (target === 'anonymous') {
      // Stale/invalid token in embed mode → drop it and browse anonymously.
      dropToken();
      goAnonymous();
      return;
    }
    if (target === 'expired') {
      // Deliberately does NOT drop the token: `wallet.error` collapses a 401
      // and a plain network failure into the same string (WalletProvider), so
      // clearing here would destroy a still-valid session on a connectivity
      // blip. A real 401 reaches the onSessionExpired handler below — from the
      // startup balance call too — and that path is the one that drops it.
      emit('session_expired');
      goExpired();
      return;
    }

    emit('session_ready', {
      user_id: 0,
      balance_cents: wallet.balance_cents ?? 0,
    });
    if (target === 'game') goGame();
    else goLobby();
  }, [
    session.token,
    session.gameId,
    session.embedKey,
    dropToken,
    wallet.loading,
    wallet.error,
    wallet.balance_cents,
    goLobby,
    goGame,
    goExpired,
    goAnonymous,
  ]);

  // Token adoption while browsing anonymously (auth modal success): re-arm
  // the init effect so it re-routes into the authed flow. Declared AFTER the
  // init effect on purpose — on the adoption render the init effect still
  // sees didInit=true and skips; WalletProvider's token-keyed refetch then
  // flips wallet.loading, re-running the init effect with didInit=false so
  // it routes once the balance has settled.
  useEffect(() => {
    if (session.token && view === 'anonymous') {
      didInit.current = false;
    }
  }, [session.token, view]);

  // Global mid-gameplay expiry: a 401 from ANY API call (purchase / reveal /
  // sell) triggers triggerSessionExpired() in the ApiClient, which fires this
  // handler. The module-level dedupe means it runs once — and won't double-emit
  // with the startup expiry path above.
  useEffect(
    () =>
      onSessionExpired(() => {
        if (decideExpiryView({ hasEmbedKey: session.embedKey != null }) === 'anonymous') {
          // Embed mode: an expired session falls back to anonymous browsing;
          // the player signs back in through the UI gates (no auto-modal).
          // Re-arm the one-shot latch so a LATER expiry (after re-login)
          // routes back here again.
          dropToken();
          goAnonymous();
          resetSessionExpired();
        } else {
          // Same reason as the startup path: a persisted token must not
          // outlive the 401 that killed it.
          dropToken();
          emit('session_expired');
          goExpired();
        }
      }),
    [goExpired, goAnonymous, session.embedKey, dropToken],
  );

  if (view === 'loading') return <LoadingScreen />;
  if (view === 'expired') {
    // A `/demo/:id` link stripped of its `?embed_key=` lands here with no
    // session it ever had — say so instead of "session expired".
    if (!session.token && !session.embedKey && getViewFromURL() === 'demo') {
      return <ExpiredScreen reason="demo-link" />;
    }
    if (session.embedKey) {
      return (
        <ExpiredScreen
          onSignIn={() => {
            didInit.current = false;
            goAnonymous();
            void requestAuth();
          }}
        />
      );
    }
    return <ExpiredScreen />;
  }

  if (gameKind.loading) return <LoadingScreen />;

  // The preview's lab state: the real lab on a client that writes nothing, on the game's own custom pack or, for any
  // other game, the demo pack.
  if (labScene !== null) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <LabPreviewPage
          client={client}
          scene={labScene}
          offer={gameKind.kind === 'custom_pack' ? gameKind.offer : null}
        />
      </Suspense>
    );
  }

  // A custom pack has no price, odds or tiers for the loot-pack pages below to show, so it never reaches
  // them. 'anonymous' and 'game' return this same element: an embed sign-in switching the view keeps the lab.
  if (gameKind.kind === 'custom_pack') {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <CustomPackLabPage client={client} offer={gameKind.offer} />
      </Suspense>
    );
  }

  if (template !== 'classic') {
    // Modern collapses lobby+game into a single carousel page. The carousel
    // itself acts as the lobby — swiping between packs picks the active gameId.
    // The 'anonymous' view renders this same branch: data flows through the
    // client's public/ rewrite; auth gates open the sign-in modal on demand.
    // Sleek and glass are forks of modern's page tree, so they boot the same way.
    const CarouselPackPage = CAROUSEL_PACK_PAGES[template];
    return (
      <CarouselPackPage
        client={client}
        // `/demo/:offer_id` names the pack in the path, so a cold demo
        // link lands on that pack instead of the category default.
        initialGameId={session.gameId ?? getDemoOfferIdFromURL() ?? selectedOfferId ?? null}
      />
    );
  }

  const classicGameId = session.gameId ?? selectedOfferId;
  if (view === 'lobby' || (view === 'anonymous' && !classicGameId)) {
    return (
      <ClassicLobby
        client={client}
        onSelect={(offerId) => {
          setSelectedOfferId(offerId);
          setGameIdInURL(offerId);
          goGame();
        }}
      />
    );
  }
  if (view === 'game' || view === 'anonymous') {
    if (!classicGameId) return null;
    return (
      <ClassicPackPage
        client={client}
        gameId={classicGameId}
      />
    );
  }
  return null;
}

export function App() {
  useChildListener(); // no-op unless ?design_constructor=child
  useClickToEdit(); // ditto — wires click-to-edit selection in the Design Constructor preview
  useNavigateBackListener(); // host hardware back (Android WebView) → one in-app back step
  useEnableHapticsListener(); // app host opts in to `haptic` events (iOS WebViews can't vibrate)

  // fundingMode is sourced from getDesignSystem (fetched inside ThemedShell)
  // and threaded here so PaymentsProvider can be mounted inside WalletProvider.
  // Defaults to 'seamless' — requestTopUp is a no-op until the API confirms 'stripe'.
  const [fundingMode, setFundingMode] = useState<FundingMode>('seamless');

  // Sourced from getDesignSystem alongside fundingMode. Defaults to false —
  // the BalancePill chooser only offers Withdraw once the API confirms the
  // operator permits it (and only when stripe-funded).
  const [allowWithdrawals, setAllowWithdrawals] = useState(false);

  // Sourced from getDesignSystem as well. 0 (every operator without the
  // setting) keeps WalletProvider from installing any keepalive timer.
  const [balanceKeepaliveSec, setBalanceKeepaliveSec] = useState(0);

  // Sourced from getDesignSystem too. The default keeps every auth gate
  // closed (registered, nothing required) until the API says otherwise —
  // legacy operators never see the sign-in modal.
  const [registration, setRegistration] = useState<RegistrationInfo>({
    mode: 'disabled',
    registered: true,
  });

  // `/animation-preview` is what the Operator Console's Pack Opening page
  // embeds: the opening mechanic on mock data. No session and no API — the
  // console posts everything in (see @game-platform/pack-animation, preview.ts).
  // Not gated by `devConsole`: the production console needs it too.
  if (window.location.pathname === '/animation-preview') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <AnimationPreviewPage />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // /design-system is a dev-only standalone route. We mount the full
  // provider stack here too — minus the Inner main flow which would
  // render the ExpiredScreen on missing token — so DevConsole can run
  // (some of its tabs need Session/Wallet contexts).
  if (config.devConsole && window.location.pathname === '/design-system') {
    return (
      <ErrorBoundary>
        <SessionProvider>
          <ThemeProvider>
            <WalletProvider>
              <Suspense fallback={<LoadingScreen />}>
                <DesignSystemPage />
              </Suspense>
              <Suspense fallback={null}>
                <DevConsole />
              </Suspense>
            </WalletProvider>
          </ThemeProvider>
        </SessionProvider>
      </ErrorBoundary>
    );
  }

  // `/shipping` is a standalone top-level route (parent opens it directly with
  // a session token). It shares the full provider stack with the game flow —
  // only the inner component differs — so auth/theme/wallet bootstrap once.
  const isShipping = window.location.pathname.startsWith('/shipping');

  return (
    <ErrorBoundary>
      <SessionProvider>
        <ThemedShell
          onFundingMode={setFundingMode}
          onAllowWithdrawals={setAllowWithdrawals}
          onRegistration={setRegistration}
          onBalanceKeepalive={setBalanceKeepaliveSec}
        >
          <WalletProvider balanceKeepaliveSec={balanceKeepaliveSec}>
            <AuthProvider registration={registration}>
              <PaymentsProvider
                fundingMode={fundingMode}
                currency="USD"
                allowWithdrawals={allowWithdrawals}
              >
                {isShipping ? <ShippingInner /> : <Inner />}
                {/* Toasts are portalled to <body>, so `#root`'s safe-area
                    padding never reaches them — sonner pins its list a flat
                    offset from the viewport top, which on a host app drawing
                    behind the status bar puts the pill under the notch. Fold
                    the page inset into both offsets; 24/16px are sonner's own
                    defaults for desktop / ≤600px, kept so nothing moves
                    where there is no inset. An operator's top-offset knob
                    replaces both. */}
                <Toaster
                  position="top-center"
                  offset={{ top: 'calc(var(--co-Notification-offsetTop, 24px) + var(--page-safe-top, 0px))' }}
                  mobileOffset={{ top: 'calc(var(--co-Notification-offsetTop, 16px) + var(--page-safe-top, 0px))' }}
                />
                {config.devConsole && (
                  <Suspense fallback={null}>
                    <DevConsole />
                  </Suspense>
                )}
              </PaymentsProvider>
            </AuthProvider>
          </WalletProvider>
        </ThemedShell>
      </SessionProvider>
    </ErrorBoundary>
  );
}

/**
 * Bridges the backend per-operator design_system into ThemeProvider:
 * once the URL session token is parsed, fetch the operator's theme
 * once and feed it as the `designSystem` + `template` props. ThemeProvider
 * re-applies the v2 ConstructorValue + template whenever the props
 * transition from undefined to a value. Fetch is soft-fail
 * (`{ template: 'classic', designSystem: null }` on any error) so the
 * iframe still boots with Figma defaults if the call fails.
 */
function ThemedShell({
  children,
  onFundingMode,
  onAllowWithdrawals,
  onRegistration,
  onBalanceKeepalive,
}: {
  children: ReactNode;
  onFundingMode?: (mode: FundingMode) => void;
  onAllowWithdrawals?: (allow: boolean) => void;
  onRegistration?: (registration: RegistrationInfo) => void;
  onBalanceKeepalive?: (sec: number) => void;
}) {
  const session = useSession();
  const [resolved, setResolved] = useState<DesignSystemResult | undefined>(undefined);

  useEffect(() => preloadForPackOverrides(), []);

  useEffect(() => {
    // Provably-fair tickets belong to the session that asked for them.
    clearTickets();
    // Embed mode fetches anonymously (the client rewrites the GET to the
    // public/ twin). Depending on `session.token` refires this on token
    // adoption so `registration.registered` reflects the session's user.
    if (!session.token && !session.embedKey) return;
    const client = createApiClient({
      baseUrl: config.apiUrl,
      getToken: session.getToken,
      getEmbedKey: () => session.embedKey,
    });
    let cancelled = false;
    // Warm the footer-links cache alongside the theme fetch: pages without a
    // footer (Pack page disclaimer, refund notice) still need the operator's
    // terms link, and this is the earliest point with a client available.
    // With a token this forces a refresh so links fetched anonymously in
    // embed mode are replaced with the session's after sign-in.
    void ensureFooterLinks(client, { refresh: session.token !== null });
    void getDesignSystem(client).then((ds) => {
      if (!cancelled) {
        saveShippingDisabled(ds.disableShipping);
        saveOperatorLinks({ lobbyUrl: ds.lobbyUrl, depositUrl: ds.depositUrl });
        saveOpeningAnimation(ds.animation);
        // Session-token launches carry no `?embed_key=`; this is where they
        // pick up the key the anonymous demo rip endpoint resolves on.
        saveDemoEmbedKey(ds.demoEmbedKey);
        saveFairnessEnabled(ds.provablyFair);
        initTracking(ds.trackingPixels);
        // A library mechanic means a Pixi chunk: fetch it now, while the
        // player is still browsing, so the scene mounts the moment a pack is
        // bought instead of after a download
        if (isOpenerVariant(ds.animation.variant)) preloadOpener();
        onFundingMode?.(ds.fundingMode);
        onAllowWithdrawals?.(ds.allowWithdrawals);
        onRegistration?.(ds.registration);
        onBalanceKeepalive?.(ds.balanceKeepaliveSec);
        setResolved(ds);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    session.token,
    session.getToken,
    session.embedKey,
    onFundingMode,
    onAllowWithdrawals,
    onRegistration,
    onBalanceKeepalive,
  ]);

  return (
    <ThemeProvider
      designSystem={resolved?.designSystem ?? undefined}
      template={resolved?.template}
    >
      {children}
    </ThemeProvider>
  );
}
